import SearchIcon from '@mui/icons-material/Search'
import { Autocomplete, Box, FormControl, FormLabel, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid2'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import UserCard from '../components/cards/UserCard'
import SEOHead from '../components/common/SEOHead'
import { useUsers } from '../contexts/UserContext'
import { instructorsAtom } from '../domains/user/AtomUsers'
import { SPORTS } from '../share/Constant'
import { getSportProps } from '../utils/utility'

const SPORT_TYPES = [getSportProps(SPORTS.SNOWBOARD), getSportProps(SPORTS.SKI)]

export default function Instructors() {
  const instructors = useAtomValue(instructorsAtom)
  const { fetchInstructorLoading, fetchInstructorError, initInstructors } = useUsers()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSportType, setSelectedSportType] = useState('')
  const [selectedCertificates, setSelectedCertificates] = useState([])

  // 从所有讲师中收集所有证书（去重并排序）
  const availableCertificates = useMemo(() => {
    const certificateSet = new Set()
    instructors.forEach((instructor) => {
      const certificates = instructor.certificates || []
      certificates.forEach((cert) => {
        if (cert) {
          certificateSet.add(cert)
        }
      })
    })
    return Array.from(certificateSet).sort()
  }, [instructors])

  useEffect(() => {
    initInstructors()
  }, [initInstructors])

  // 处理运动类型选择
  const handleSportTypeChange = (typeValue) => {
    setSelectedSportType((prev) => (prev === typeValue ? '' : typeValue))
  }

  // 处理证书选择（多选）
  const handleCertificateChange = (_event, newValue) => {
    setSelectedCertificates(newValue || [])
  }

  // 根据搜索词、运动类型和证书筛选讲师
  const filteredInstructors = useMemo(() => {
    return instructors.filter((instructor) => {
      // 搜索筛选（按姓名或简介）
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        const nameMatch = instructor.name?.toLowerCase().includes(searchLower)
        const bioMatch = instructor.bio?.toLowerCase().includes(searchLower)
        if (!nameMatch && !bioMatch) {
          return false
        }
      }

      // 运动类型筛选
      if (selectedSportType) {
        const instructorSports = instructor.sports || []
        const hasSport = instructorSports.some((sport) => sport.toUpperCase() === selectedSportType.toUpperCase())
        if (!hasSport) {
          return false
        }
      }

      // 证书筛选（多选 - OR 逻辑：如果讲师有任意一个选中的证书就显示）
      if (selectedCertificates.length > 0) {
        const instructorCertificates = instructor.certificates || []
        const hasAnyCertificate = selectedCertificates.some((selectedCert) => instructorCertificates.some((cert) => cert === selectedCert))
        if (!hasAnyCertificate) {
          return false
        }
      }

      return true
    })
  }, [instructors, searchTerm, selectedSportType, selectedCertificates])

  if (fetchInstructorLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (fetchInstructorError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchInstructorError}
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    )
  }

  return (
    <>
      <SEOHead pageName="INSTRUCTORS" url="https://bookingonsnow.com/instructors" structuredDataType="Organization" />
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>
          Our Instructors
        </Typography>

        {/* 搜索框 */}
        <FormControl fullWidth sx={{ mb: 3, maxWidth: 600 }}>
          <FormLabel
            sx={{
              mb: 2,
              fontWeight: 700,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              letterSpacing: '0.15rem',
            }}
          >
            Search Instructors
          </FormLabel>
          <TextField
            fullWidth
            placeholder="Search by name or bio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '2.5rem',
              },
            }}
          />
        </FormControl>

        {/* 运动类型筛选器 */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <FormLabel
            sx={{
              mb: 2,
              fontWeight: 700,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              letterSpacing: '0.15rem',
            }}
          >
            Filter by Sport Type
          </FormLabel>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
            {SPORT_TYPES.map((type) => (
              <Button
                key={type.value}
                variant={selectedSportType === type.value ? 'contained' : 'outlined'}
                onClick={() => handleSportTypeChange(type.value)}
                startIcon={type.icon}
                sx={{
                  borderRadius: 8,
                  textTransform: 'none',
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  borderWidth: 1,
                  '& .MuiButton-startIcon': {
                    mr: 0.5,
                  },
                }}
              >
                {type.label}
              </Button>
            ))}
          </Stack>
        </FormControl>

        {/* 证书筛选器 */}
        {availableCertificates.length > 0 && (
          <FormControl fullWidth sx={{ mb: 3, maxWidth: 600 }}>
            <FormLabel
              sx={{
                mb: 2,
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: '0.15rem',
              }}
            >
              Filter by Certificate
            </FormLabel>
            <Autocomplete
              multiple
              size="large"
              options={availableCertificates}
              value={selectedCertificates}
              onChange={handleCertificateChange}
              filterSelectedOptions
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select certificates"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      minHeight: '2.5rem',
                      height: 'auto',
                      padding: '6px 12px',
                    },
                  }}
                />
              )}
            />
          </FormControl>
        )}

        {/* 显示筛选结果数量 */}
        {(searchTerm || selectedSportType || selectedCertificates.length > 0) && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filteredInstructors.length} instructor{filteredInstructors.length !== 1 ? 's' : ''}
          </Typography>
        )}

        {/* 讲师列表 */}
        {filteredInstructors.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No instructors found matching the selected filters.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            {filteredInstructors.map((instructor) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={instructor.resourceId}>
                <Box sx={{ height: '100%' }}>
                  <UserCard user={instructor} size="medium" sx={{ height: '100%', maxWidth: '600px' }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </>
  )
}
